import CopyButton from "@components/buttons/CopyButton";
import LabelRow from "@components/rowcomponents/LabelRow";
import TruncatedCopyValue from "@components/rowcomponents/TruncatedCopyValue";
import { primeServices } from "@services/apis/Prime";
import { transactionApiServices } from "@services/apis/Transactions";
import {
  GetPrimePHPTradeDetailsData,
  GetPrimePHPTradeDetailsResponse,
} from "@services/models/Prime/GetPrimePHPTradeDetailsModel";
import {
  SettleTransactionRequest,
  SettleTransactionResponse,
} from "@services/models/Transactions/SettleTransactionModel";
import React, { useEffect, useState } from "react";
import webSocketService from "../../services/websocket/WebsocketServices";
import useSnackbarMessage from "../../utils/HandleSuccessErrorSnackbars";
import InvertibleDateTime from "./components/InvertibleDateTime";
import InvertibleRate from "./components/InvertibleRate";
import PrimeDetailsActionButton from "./components/PrimeDetailsActionButton";
import PrimeOrderFlowDetailsSkeleton from "./components/PrimeOrderFlowDetailsSkeleton";
import {
  getPhilippinesEventPillClass,
  OrderDetailsPills,
} from "./components/PrimeOrderPills";

interface PrimePhilippinesOrderFlowDetailsProps {
  instanceId: string;
}

export const PrimePhilippinesOrderFlowDetails: React.FC<
  PrimePhilippinesOrderFlowDetailsProps
> = ({ instanceId }) => {
  const [data, setData] = useState<GetPrimePHPTradeDetailsData>();
  const [error, setError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const response: GetPrimePHPTradeDetailsResponse =
        await primeServices.getPHPInstanceDetails({ instanceId });

      const processedData = {
        ...response.data,
      };

      setData(processedData);
    } catch (error: any) {
      setError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const [isButtonLoading, setIsButtonLoading] = useState<boolean>(false);
  const { handleError, handleSuccess } = useSnackbarMessage();

  const handleSendCallback = async () => {
    try {
      setIsButtonLoading(true);
      const settleTransactionRequest: SettleTransactionRequest = {
        instanceId: instanceId,
      };

      const response: SettleTransactionResponse =
        await transactionApiServices.settleTransaction(
          settleTransactionRequest,
          "/adminPanel/sendPHPWebhook",
        );
      handleSuccess({
        message: response.msg || "Transaction settled successfully",
      });
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.msg ||
        "Updating status failed. Please try again.";
      handleError({ message: errorMessage });
    } finally {
      setIsButtonLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [instanceId]);

  const handleTradeChangeWebSockets = (
    socketData: GetPrimePHPTradeDetailsData,
  ) => {
    if (socketData.instanceId === instanceId) {
      const processedData = {
        ...socketData,
      };
      setData(processedData);
    }
  };

  useEffect(() => {
    const initializeWebSocket = async () => {
      try {
        webSocketService.onMessage(
          "php_instance_details",
          true,
          handleTradeChangeWebSockets,
        );
      } catch (error) {
        console.error("WebSocket initialization error:", error);
      }
    };

    initializeWebSocket();
  }, [webSocketService.protectedSocket?.connected]);

  if (error) {
    return <div>Fetch error</div>;
  }

  if (isLoading) {
    return <PrimeOrderFlowDetailsSkeleton width="w-full" />;
  }

  const tradeTypeLabel =
    data?.isBuying === 1 || data?.isBuying === true ? "BUY" : "SELL";

  return (
    <div
      className="flex flex-col gap-[12px] mt-[20px]"
      style={{ height: "65dvh" }}
    >
      <div className="flex flex-col py-[8px] px-[12px] space-y-[16px] border border-border-subtle bg-bg-base rounded-lg">
        <div className="flex flex-col gap-3">
          <p className="text-text-navbar-active text-[12px] font-semibold underline">
            Merchant Details:
          </p>
          <LabelRow
            label="Merchant ID | User ID"
            valueComp={
              <div className="flex flex-row gap-[4px] items-center">
                <span className="text-text-base text-[12px] font-normal">
                  {data?.merchantId}
                </span>
                <CopyButton
                  copyText={data?.merchantId?.toString() ?? ""}
                  copyMessage="Copied to clipboard"
                />
                <span className="text-text-base text-[12px] font-normal">
                  |
                </span>
                <span className="text-text-base text-[12px] font-normal">
                  {data?.userId}
                </span>
                <CopyButton
                  copyText={(data?.userId || "").toString()}
                  copyMessage="Copied to clipboard"
                />
              </div>
            }
          />
          <LabelRow label="Merchant Name" value={data?.merchantName} />
          <p className="text-text-navbar-active text-[12px] font-semibold underline">
            Sub Merchant Details:
          </p>

          <LabelRow
            label="Merchant Name"
            value={data?.merchantDetails?.merchantName ?? "- -"}
            isCopy={!!data?.merchantDetails?.merchantName}
          />

          <LabelRow
            label="Merchant Internal ID"
            value={data?.merchantDetails?.merchantInternalId ?? "- -"}
            isCopy={!!data?.merchantDetails?.merchantInternalId}
          />

          <LabelRow
            label="Merchant URL"
            value={data?.merchantDetails?.merchantUrl ?? "- -"}
            isCopy={!!data?.merchantDetails?.merchantUrl}
          />
        </div>
      </div>

      <div className="flex flex-col py-[8px] px-[12px] space-y-[16px] border border-border-subtle bg-bg-base rounded-lg">
        <div className="flex flex-col gap-3">
          <div className="flex flex-row items-center justify-between">
            <p className="text-text-navbar-active text-[12px] font-semibold underline">
              Order Details:
            </p>
            <OrderDetailsPills
              tradeType={tradeTypeLabel}
              eventId={data?.eventId}
              eventClassName={getPhilippinesEventPillClass(data?.eventId)}
            />
          </div>
          <LabelRow
            label="Merchant Ref ID"
            value={data?.merchantRefId ?? ""}
            valueComp={
              <TruncatedCopyValue
                value={data?.merchantRefId}
                copyMessage="Merchant Ref ID copied to clipboard"
              />
            }
          />
          <LabelRow
            label="Instance ID"
            value={data?.instanceId ?? ""}
            isCopy={!!data?.instanceId}
            valueComp={
              <div className="flex flex-row gap-[4px] items-center">
                <span className="text-text-base text-[12px] font-normal">
                  {data?.instanceId && data?.instanceId.substring(0, 8)}...
                </span>
                <CopyButton
                  copyText={data?.instanceId ?? ""}
                  copyMessage="Copied to clipboard"
                />
              </div>
            }
          />
          <LabelRow
            label="Ext URL"
            value={data?.extUrl ?? ""}
            valueComp={
              <TruncatedCopyValue
                value={data?.extUrl}
                copyMessage="Ext URL copied to clipboard"
                openInNewTab
              />
            }
          />
          <InvertibleDateTime value={data?.createdAt} />
        </div>
        <div className="flex flex-row items-center gap-2">
          <PrimeDetailsActionButton
            label="Send callback"
            icon="send"
            onClick={handleSendCallback}
            loading={isButtonLoading}
          />
        </div>
      </div>

      <div className="flex flex-col py-[8px] px-[12px] space-y-[12px] border border-border-subtle bg-bg-base rounded-lg">
        <p className="text-text-navbar-active text-[12px] font-semibold underline">
          Trade Details:
        </p>
        <LabelRow
          label="Token | Fiat"
          valueComp={
            <div className="flex flex-row gap-[4px] items-center">
              <img src={data?.cryptoCurrencyImage} height={12} width={12} />
              <span className="text-text-base text-[12px] font-normal">
                {data?.cryptoCurrencySymbol}
              </span>
              <span className="text-text-base text-[12px] font-normal">|</span>
              <img src={data?.fiatCurrencyImage} height={12} width={12} />
              <span className="text-text-base text-[12px] font-normal">
                {data?.fiatCurrencySymbol}
              </span>
            </div>
          }
        />
        <LabelRow
          label="Amount in VDA | Fiat"
          valueComp={
            <div className="flex flex-row gap-[4px] items-center">
              <span className="text-text-base text-[12px] font-normal">
                {Number(data?.cryptoAmount ?? 0).toFixed(2)}
              </span>
              <img src={data?.cryptoCurrencyImage} height={12} width={12} />
              <span className="text-text-base text-[12px] font-normal">|</span>
              <span className="text-text-base text-[12px] font-normal">
                {Number(data?.fiatAmount ?? 0).toFixed(2)}
              </span>
              <img src={data?.fiatCurrencyImage} height={12} width={12} />
            </div>
          }
        />
        <LabelRow
          label="Rate"
          valueComp={
            <InvertibleRate
              cryptoToFiatRate={
                data?.rate != null ? 1 / Number(data?.rate) : undefined
              }
              cryptoImage={data?.cryptoCurrencyImage}
              cryptoSymbol={data?.cryptoCurrencySymbol}
              fiatImage={data?.fiatCurrencyImage}
              fiatSymbol={data?.fiatCurrencySymbol}
            />
          }
        />
        <LabelRow
          label="Fees"
          valueComp={
            <div className="flex flex-row gap-[4px] items-center">
              <span className="text-text-base text-[12px] font-normal">
                {Number(data?.fees || 0).toFixed(2)}
              </span>
              <img src={data?.cryptoCurrencyImage} height={12} width={12} />
            </div>
          }
        />
        <LabelRow
          label="MDR"
          value={`${Number(data?.MDR || 0).toFixed(2)} %`}
        />
      </div>
    </div>
  );
};

export default PrimePhilippinesOrderFlowDetails;
